import {
  atom,
  useRecoilState,
  useRecoilValue,
  selector,
  useResetRecoilState
} from 'recoil'

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface FiltersState {
  dateRange: DateRange;
  verbatim: string;
  provider: string;
}

interface SortState {
  by: string;
  direction: 'asc' | 'desc';
}

interface QueryVariables {
  searchTerm: string;
  provider: string;
  dateFrom: string | null;
  dateTo: string | null;
  sortField: string;
  sortOrder: string;
}

const filtersState = atom<FiltersState>({
  key: 'WANFilters',
  default: {
    dateRange: {
      start: null,
      end: null,
    },
    verbatim: '',
    provider: '',
  },
})

const sortState = atom<SortState>({
  key: 'WANSort',
  default: {
    by: 'wifirstsite.name.raw',
    direction: 'asc',
  },
})

const queryVariablesState = selector<QueryVariables>({
  key: 'WANQueryVariables',
  get: ({ get }) => {
    const filters = get(filtersState)
    const sort = get(sortState)

    const dateFrom = filters.dateRange.start ? filters.dateRange.start.toISOString() : null;
    const dateTo = filters.dateRange.end ? filters.dateRange.end.toISOString() : null;

    const isDefaultSort = sort.by === 'wifirstsite.name.raw' && sort.direction === 'asc';

    return {
      searchTerm: filters.verbatim.length > 2 ? filters.verbatim : '',
      provider: filters.provider,
      dateFrom,
      dateTo,
      sortField: isDefaultSort ? '' : sort.by,
      sortOrder: isDefaultSort ? '' : sort.direction,
    }
  },
})

const useFilters = () => {
  return useRecoilState(filtersState)
}

export const useResetFilters = () => {
  return useResetRecoilState(filtersState)
}

export const useQueryVariables = () => useRecoilValue(queryVariablesState)

export const useSort = () => {
  const [{ direction, by }, setSort] = useRecoilState(sortState)
  return [
    { direction, by },
    (sort: string) => {
      if (by === sort) {
        const newDirection = direction === 'asc' ? 'desc' : 'asc'
        setSort({ by: sort, direction: newDirection })
      } else {
        setSort({ by: sort, direction: 'asc' })
      }
    },
  ] as const
}

export default useFilters
